import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Space,
  Button,
  Input,
  Select,
  Pagination,
  message,
  Modal,
  Image,
  Badge,
  Empty,
} from 'antd';
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import copy from 'copy-to-clipboard';
import { filesApi } from '../services/api';
import { isImage, getFileUrl } from '../utils/format';
import VirtualFileList, { VirtualGridFileList } from './VirtualFileList';
import type { FileRecord, FileListQuery, ViewMode } from '../types';

const { confirm } = Modal;

interface FileListProps {
  onRefresh?: () => void;
  colors: {
    primary: string;
    success: string;
    text: string;
    textMuted: string;
    border: string;
    surface: string;
    background: string;
    hover: string;
  };
}

export default function FileList({ onRefresh, colors }: FileListProps) {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [query, setQuery] = useState<FileListQuery>({
    page: 1,
    pageSize: 50, // 增加每页数量，虚拟列表可以处理大量数据
    category: 'all',
    sortBy: 'createdAt',
    order: 'desc',
  });
  const [total, setTotal] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [listHeight, setListHeight] = useState(500);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await filesApi.getFiles(query);
      
      if (response && typeof response === 'object' && 'data' in response) {
        const data = (response as any).data as { files: FileRecord[]; total?: number };
        setFiles(data.files || []);
        setTotal(data.total || 0);
        setSelectedFiles([]);
      }
    } catch (error) {
      message.error('加载文件列表失败');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // 计算列表容器高度
  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        // 预留空间给工具栏、分页等
        const reservedHeight = 250;
        const availableHeight = Math.max(300, windowHeight - containerRect.top - reservedHeight);
        setListHeight(availableHeight);
      }
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const isAllSelected = useMemo(() => {
    return files.length > 0 && files.every(file => selectedFiles.includes(file.id));
  }, [files, selectedFiles]);

  const isIndeterminate = useMemo(() => {
    return selectedFiles.length > 0 && selectedFiles.length < files.length;
  }, [selectedFiles, files]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedFiles(files.map(file => file.id));
    } else {
      setSelectedFiles([]);
    }
  }, [files]);

  const handleSelectFile = useCallback((fileId: string, checked: boolean) => {
    if (checked) {
      setSelectedFiles(prev => [...prev, fileId]);
    } else {
      setSelectedFiles(prev => prev.filter(id => id !== fileId));
    }
  }, []);

  const handleBatchDelete = useCallback(() => {
    if (selectedFiles.length === 0) {
      message.warning('请先选择要删除的文件');
      return;
    }

    confirm({
      title: '确认删除',
      content: `确定删除选中的 ${selectedFiles.length} 个文件吗？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await filesApi.batchDeleteFiles(selectedFiles);
          message.success(`已删除 ${selectedFiles.length} 个文件`);
          setSelectedFiles([]);
          fetchFiles();
          onRefresh?.();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  }, [selectedFiles, fetchFiles, onRefresh]);

  const handleDelete = useCallback((file: FileRecord) => {
    confirm({
      title: '确认删除',
      content: `删除 "${file.fileName}"？`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await filesApi.deleteFile(file.id);
          message.success('删除成功');
          fetchFiles();
          onRefresh?.();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  }, [fetchFiles, onRefresh]);

  const handleDownload = useCallback(async (file: FileRecord) => {
    try {
      const response = await filesApi.downloadFile(file.id);
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('下载成功');
      }
    } catch (error) {
      message.error('下载失败');
    }
  }, []);

  const handleCopyUrl = useCallback((file: FileRecord) => {
    const url = getFileUrl(file.fileUrl, file.filePath);
    if (!url) {
      message.error('文件URL不存在');
      return;
    }
    
    const success = copy(url);
    if (success) {
      message.success('链接已复制');
    } else {
      message.error('复制失败');
    }
  }, []);

  const handlePreview = useCallback((file: FileRecord) => {
    const url = getFileUrl(file.fileUrl, file.filePath);
    if (!url) {
      message.error('文件URL不存在');
      return;
    }
    
    if (isImage(file.fileType)) {
      setPreviewImage(url);
    } else if (file.fileType.startsWith('video/')) {
      window.open(url, '_blank');
    } else {
      message.info('该文件类型不支持预览');
    }
  }, []);



  return (
    <div style={{ width: '100%' }}>
      {/* Toolbar */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <Input
            placeholder="搜索文件名"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={() => setQuery(prev => ({ ...prev, keyword: searchKeyword, page: 1 }))}
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
          />
          <Select
            value={query.category}
            onChange={(v) => setQuery(prev => ({ ...prev, category: v, page: 1 }))}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: '全部文件' },
              { value: 'image', label: '图片' },
              { value: 'video', label: '视频' },
              { value: 'document', label: '文档' },
            ]}
          />
        </Space>

        <Space>
          <Button.Group>
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('grid')}
            >
              网格
            </Button>
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('list')}
            >
              列表
            </Button>
          </Button.Group>
        </Space>
      </Space>

      {/* Batch Actions */}
      {selectedFiles.length > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: colors.background,
          borderRadius: 6,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Space>
            <Badge count={selectedFiles.length} style={{ backgroundColor: colors.primary }} />
            <span style={{ color: colors.text }}>已选择</span>
          </Space>
          <Space>
            <Button onClick={() => setSelectedFiles([])}>取消</Button>
            <Button type="primary" danger onClick={handleBatchDelete}>
              批量删除
            </Button>
          </Space>
        </div>
      )}

      {/* File List */}
      <div ref={containerRef} style={{ minHeight: 300 }}>
        {loading ? (
          <div style={{ 
            height: listHeight, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Empty description="加载中..." />
          </div>
        ) : files.length === 0 ? (
          <div style={{ 
            height: listHeight, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Empty description="暂无文件" />
          </div>
        ) : viewMode === 'list' ? (
          <VirtualFileList
            files={files}
            selectedFiles={selectedFiles}
            colors={colors}
            onSelectFile={handleSelectFile}
            onSelectAll={handleSelectAll}
            onPreview={handlePreview}
            onCopyUrl={handleCopyUrl}
            onDownload={handleDownload}
            onDelete={handleDelete}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            containerHeight={listHeight}
          />
        ) : (
          <VirtualGridFileList
            files={files}
            selectedFiles={selectedFiles}
            colors={colors}
            onSelectFile={handleSelectFile}
            onPreview={handlePreview}
            onCopyUrl={handleCopyUrl}
            onDownload={handleDownload}
            onDelete={handleDelete}
            onSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            containerHeight={listHeight}
            columnCount={4}
          />
        )}
      </div>

      {/* Pagination */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Pagination
          current={query.page}
          pageSize={query.pageSize}
          total={total}
          onChange={(page, pageSize) => setQuery(prev => ({ ...prev, page, pageSize: pageSize || 20 }))}
          showSizeChanger
          showTotal={(total) => `共 ${total} 个文件`}
        />
      </div>

      {/* Image Preview */}
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: !!previewImage,
          src: previewImage || '',
          onVisibleChange: (visible) => !visible && setPreviewImage(null),
        }}
      />
    </div>
  );
}
